/**
 * Hook to monitor network status and connection quality
 */
import { useState, useEffect, useCallback } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  connectionType: string;
  effectiveType: string;
  downlink: number | null;
  rtt: number | null; // Round-trip time in ms
}

export function useNetworkStatus() {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isSlowConnection: false,
    connectionType: 'unknown',
    effectiveType: 'unknown',
    downlink: null,
    rtt: null,
  });

  const updateNetworkInfo = useCallback(() => {
    if (typeof navigator === 'undefined') return;

    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    const isOnline = navigator.onLine;
    let isSlowConnection = false;
    let connectionType = 'unknown';
    let effectiveType = 'unknown';
    let downlink = null;
    let rtt = null;

    if (connection) {
      effectiveType = connection.effectiveType || 'unknown';
      connectionType = connection.type || 'unknown';
      downlink = connection.downlink || null;
      rtt = connection.rtt || null;

      // Consider connection slow if:
      // - Effective type is 'slow-2g' or '2g'
      // - RTT > 1000ms
      // - Downlink < 0.5 Mbps
      isSlowConnection = 
        effectiveType === 'slow-2g' ||
        effectiveType === '2g' ||
        (rtt !== null && rtt > 1000) ||
        (downlink !== null && downlink < 0.5);
    }

    setNetworkStatus({
      isOnline,
      isSlowConnection,
      connectionType,
      effectiveType,
      downlink,
      rtt,
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Update immediately
    updateNetworkInfo();

    // Listen for online/offline events
    const handleOnline = () => {
      console.log('[Network] 🟢 Connection restored');
      updateNetworkInfo();
    };

    const handleOffline = () => {
      console.log('[Network] 🔴 Connection lost');
      updateNetworkInfo();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for connection changes
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (connection) {
      connection.addEventListener('change', updateNetworkInfo);
    }

    // Poll network status every 30 seconds as a fallback
    const interval = setInterval(updateNetworkInfo, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', updateNetworkInfo);
      }
      clearInterval(interval);
    };
  }, [updateNetworkInfo]);

  return networkStatus;
}

/**
 * Hook to detect when network comes back online
 */
export function useOnlineDetection(callback: () => void) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      console.log('[Network] Connection restored, executing callback');
      callback();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [callback]);
}

